import { Type } from "@themes/pollinator/ui/Type";
import { marketingAccent, marketingType } from "../../marketing.type";

export type Stat = {
  /** The figure itself. A string, because several are percentages or ranges. */
  value: string;
  label: string;
};

export type Props = {
  eyebrow?: string;
  stats: Stat[];
  /**
   * Whether the figures are still being counted. Their places are held and
   * they fade in when they arrive, rather than one set of numbers being
   * swapped for another in front of the reader.
   */
  isLoading?: boolean;
  /** The ask at the foot of the panel */
  children?: React.ReactNode;
  className?: string;
};


/**
 * The numbers, in a panel of their own.
 *
 * A figure with no caption means nothing, so the two are one unit here rather
 * than two columns that could fall out of step. Several of these figures are
 * still blanks in Outlast's copy; they are rendered as given rather than
 * guessed at, so an unfilled one is visible as an unfilled one.
 *
 * Every figure is shown at every width. The grid is two columns wide until a
 * desktop and three from there, so an odd number of figures leaves one alone
 * on the last row of the narrower grid, in the left column like the rest.
 */
export function MarketingStatPanel({
  eyebrow,
  stats,
  isLoading = false,
  children,
  className,
}: Props) {
  return (
    <div
      data-testid="marketing-stat-panel"
      className={`bg-brand-800 text-brand-50 rounded-[26px] p-6 flex flex-col gap-12 ${
        className ?? ""
      }`}
    >
      {eyebrow && (
        <Type
          variant="caption"
          as="p"
          className={`${marketingType.cardEyebrow} ${marketingAccent} self-start max-w-none`}
        >
          {eyebrow}
        </Type>
      )}
      <dl className="grid grid-cols-2 gap-x-8 gap-y-8 lg:mt-2 lg:grid-cols-3">
        {stats.map((stat) => (
          <div key={stat.label} className="flex flex-col gap-2">
            <Type
              variant="h3"
              as="dt"
              className={`${marketingType.figure} font-medium transition-opacity duration-[600ms] ${
                isLoading ? "opacity-0" : "opacity-100"
              }`}
            >
              {stat.value}
            </Type>
            <Type variant="body2" as="dd" className={`${marketingType.small} max-w-[294px] md:max-w-none lg:max-w-[294px]`}>
              {stat.label}
            </Type>
          </div>
        ))}
      </dl>
      {children && (
        <div className="border-t border-brand-700 pt-8 flex flex-col items-center text-center gap-4 lg:mt-1.5 lg:pt-[27px]">
          {children}
        </div>
      )}
    </div>
  );
}

export default MarketingStatPanel;
